import { Sparkles } from 'lucide-react';
import { memo } from 'react';

import { cn } from '@/design-system/lib/cn';
import { toneStyle } from '@/design-system/lib/tone';
import { ColorPickerButton } from '@/design-system/ui/ColorPicker';
import { MINISTRY_PALETTE, type PaletteColor, paletteOf } from '@escala/domain/palette';

const SWATCH = 'size-7.5 justify-self-center rounded-pill';
const SELECTED = 'ring-2 ring-current ring-offset-2 ring-offset-white';

/**
 * A cor do evento: "Automática" (a da série, `seriesColor` — no evento novo ainda não se
 * sabe qual, porque sai do id), as 24 da paleta ou uma livre. `null` é a automática.
 * Memorizada para não redesenhar a cada letra do título.
 */
const ColorField = memo(function ColorField({
  value,
  automatic,
  onChange,
}: {
  value: string | null;
  /** A cor automática da série, ou `null` no evento novo. */
  automatic: PaletteColor | null;
  onChange: (color: string | null) => void;
}) {
  const preset = value ? MINISTRY_PALETTE.find((c) => c.color === value.toUpperCase()) : undefined;
  const custom = !!value && !preset;
  const autoName = automatic ? `Automática (${automatic.name})` : 'Automática';
  const current = value ? paletteOf(value).name : autoName;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-small font-semibold">Cor</span>
        <span className="text-caption text-ink-subtle">{current}</span>
      </div>
      <div
        role="radiogroup"
        aria-label="Cor do evento"
        className="grid grid-cols-[repeat(auto-fill,minmax(30px,1fr))] gap-2.5 p-1"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!value}
          aria-label={autoName}
          title={autoName}
          onClick={() => onChange(null)}
          style={automatic ? toneStyle(automatic) : undefined}
          className={cn(
            SWATCH,
            'flex items-center justify-center',
            automatic
              ? 'bg-tone-tint text-tone'
              : 'border-[1.5px] border-dashed border-line-input bg-surface-subtle text-ink-subtle',
            !value && SELECTED
          )}
        >
          <Sparkles aria-hidden className="size-3.5 stroke-[1.8]" />
        </button>
        {MINISTRY_PALETTE.map((c) => {
          const on = preset?.color === c.color;

          return (
            <button
              key={c.color}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={c.name}
              title={c.name}
              onClick={() => onChange(c.color)}
              className={cn(SWATCH, on && SELECTED)}
              style={{ backgroundColor: c.color, color: c.color }}
            />
          );
        })}
        <ColorPickerButton
          value={value ?? automatic?.color ?? MINISTRY_PALETTE[0]!.color}
          onChange={onChange}
          selected={custom}
          className={SWATCH}
          selectedClassName={SELECTED}
        />
      </div>
      <span className="text-caption text-ink-subtle">
        {automatic
          ? 'A cor do evento no calendário e nos cards. Na automática, a série fica com a cor que já tinha.'
          : 'A cor do evento no calendário e nos cards. Na automática, o evento ganha uma cor da paleta ao ser criado.'}{' '}
        Cada cor tem a versão do tema escuro.
      </span>
    </div>
  );
});

export default ColorField;
